/**
 * Phase 5.9.18 — Sentry health badge.
 *
 * Tiny pulse in the dispatcher header that polls `/api/admin/sentry-triage/alerts`
 * every 60s and renders a status dot:
 *   🟢  0 open alerts                   (color = #39FF14 — healthy, icon-only, no label)
 *   🟡  1-2 open / approved alerts      (color = #FFD60A — minor)
 *   🔴  3+ open OR any FATAL/NEEDS_HUMAN (color = #FF3B30 — needs attention)
 *
 * The healthy state shows the icon + green dot only (the "OK" label was dropped
 * Feb 2026 — redundant with the icon, and operators read the colour first anyway).
 *
 * Tap → navigates to `/dispatch/settings` where SentryTriagePanel lives.
 *
 * Super-admin only — silently no-ops for other roles (the API would 403 anyway,
 * but skipping the fetch keeps the dispatcher's own console clean).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Activity } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const POLL_INTERVAL_MS = 60_000;

export function HealthBadge() {
  const { user } = useAuth();
  const [open, setOpen] = useState(0);
  const [critical, setCritical] = useState(0);
  const [hidden, setHidden] = useState(false);

  const fetchAlerts = useCallback(async () => {
    if (user?.role !== 'super_admin') return;
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/sentry-triage/alerts?limit=200`,
        { withCredentials: true },
      );
      const byStatus = data?.stats?.by_status || {};
      const byClass = data?.stats?.by_classification || {};
      // "open" + "approved_for_fix" both count as un-acknowledged
      // (an approved fix is still waiting for an agent run to actually land it,
      // so it should keep nagging the operator until reviewed/dismissed).
      const openCount = (byStatus.open || 0) + (byStatus.approved_for_fix || 0);
      const critCount = (byClass.FATAL || 0) + (byClass.NEEDS_HUMAN || 0);
      setOpen(openCount);
      setCritical(critCount);
      setHidden(false);
    } catch (err) {
      // 401/403 -> silently hide (e.g. session expired). 5xx -> also hide,
      // we don't want a flapping badge to distract during real ops.
      setHidden(true);
    }
  }, [user]);

  useEffect(() => {
    fetchAlerts();
    if (user?.role !== 'super_admin') return;
    const id = setInterval(fetchAlerts, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchAlerts, user]);

  if (user?.role !== 'super_admin' || hidden) return null;

  // Tier color
  const tier =
    critical > 0 || open >= 3 ? 'red' :
    open >= 1 ? 'yellow' :
    'green';
  const color =
    tier === 'red' ? '#FF3B30' :
    tier === 'yellow' ? '#FFD60A' :
    '#39FF14';
  // Feb 2026 (post-fix) — label always reads "SENTRY" so it's never confused
  // with the duplicate Settings link in the hamburger menu. Count appended
  // only when alerts > 0 so the badge stays compact in the healthy state.
  const label = open === 0 ? 'SENTRY' : `SENTRY ${open}`;
  const title =
    tier === 'green' ? 'Sentry monitor — 0 open alerts (tap to open triage panel)'
    : tier === 'yellow' ? `Sentry — ${open} open alert${open === 1 ? '' : 's'}`
    : `Sentry — ${open} open alerts${critical > 0 ? ` · ${critical} CRITICAL` : ''}`;

  return (
    <Link
      // Jump straight to the SentryTriagePanel inside Settings instead of the
      // top of the page (where the user can't immediately tell why they're
      // there). The anchor id lives on the panel root in SettingsPage.js.
      to="/dispatch/settings#sentry-triage"
      title={title}
      data-testid="health-badge"
      className="flex items-center gap-1 px-2 py-1 border rounded-none font-mono text-[10px] uppercase tracking-wider transition-colors hover:bg-black/30"
      style={{ borderColor: color, color }}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${tier !== 'green' ? 'animate-pulse' : ''}`}
        style={{ background: color }}
        aria-hidden="true"
      />
      <Activity className="w-3 h-3" />
      <span data-testid="health-badge-count">{label}</span>
    </Link>
  );
}
